import {
  Dispatch,
  SetStateAction,
  useCallback,
  useState,
  useRef,
  useLayoutEffect,
} from 'react';

export const isBrowser = typeof window !== 'undefined';

export const noop = () => {};

type parserOptions<T> =
  | {
      raw: true;
    }
  | {
      raw: false;
      serializer: (value: T) => string;
      deserializer: (value: string) => T;
    };

const useLocalStorage = <T>(
  key: string,
  initialValue?: T,
  options?: parserOptions<T>,
): [
  value: T | undefined,
  setValue: Dispatch<SetStateAction<T | undefined>>,
  remove: () => void,
] => {
  if (!isBrowser) {
    return [initialValue as T, noop, noop];
  }
  if (!key) {
    throw new Error('useLocalStorage key may not be falsy');
  }

  const deserializer = options
    ? options.raw
      ? (value: string) => value
      : options.deserializer
    : JSON.parse;

  const initializer = useRef((key: string) => {
    try {
      const serializer = options
        ? options.raw
          ? String
          : options.serializer
        : JSON.stringify;

      const localStorageValue = localStorage.getItem(key);
      if (localStorageValue !== null) {
        return deserializer(localStorageValue);
      } else {
        initialValue && localStorage.setItem(key, serializer(initialValue));
        return initialValue;
      }
    } catch {
      return initialValue;
    }
  });

  const [state, setState] = useState<T | undefined>(() =>
    initializer.current(key),
  );

  useLayoutEffect(() => setState(initializer.current(key)), [key]);

  const set: Dispatch<SetStateAction<T | undefined>> = useCallback(
    (valOrFunc) => {
      try {
        const newState =
          typeof valOrFunc === 'function'
            ? // eslint-disable-next-line @typescript-eslint/ban-types
              (valOrFunc as Function)(state)
            : valOrFunc;
        if (typeof newState === 'undefined') {
          return;
        }
        let value: string;

        if (options) {
          if (options.raw) {
            if (typeof newState === 'string') {
              value = newState;
            } else {
              value = JSON.stringify(newState);
            }
          } else if (options.serializer) {
            value = options.serializer(newState);
          } else {
            value = JSON.stringify(newState);
          }
        } else {
          value = JSON.stringify(newState);
        }

        localStorage.setItem(key, value);
        setState(deserializer(value));
      } catch {
        // TODO: Handle error
      }
    },
    [key, setState],
  );

  const remove = useCallback(() => {
    try {
      localStorage.removeItem(key);
      setState(undefined);
    } catch {
      // TODO: Handle error
    }
  }, [key, setState]);

  return [state, set, remove];
};

const useRemoveLocalStorage = (key: string) => {
  const [, , remove] = useLocalStorage(key);

  return remove;
};

const useLocalStorageValue = <T>(
  key: string,
  initialValue?: T,
  options?: parserOptions<T>,
) => {
  const [value] = useLocalStorage<T>(key, initialValue, options);

  return value;
};

const useSetLocalStorage = <T>(
  key: string,
  initialValue?: T,
  options?: parserOptions<T>,
) => {
  const { 1: setValue } = useLocalStorage<T>(key, initialValue, options);

  return setValue;
};

export {
  useRemoveLocalStorage,
  useLocalStorageValue,
  useSetLocalStorage,
  useLocalStorage,
};
